/**
 * What a school admin should look at first, and how each class is doing.
 *
 * Pure: the roster and the results go in, plain rows come out, so every rule
 * here is tested (tests/classroomAttention.test.mts) and the Overview only
 * draws them.
 *
 * "Activity" is read from quiz results, the one record every live lesson
 * leaves that a school may list (class_sessions are readable only by their
 * teacher). A lesson taught with no quiz leaves no trace here, so the wording
 * says "no quiz results" rather than "did not teach".
 */

import { summarise, withinRange, type DateRange, type Result } from '../analytics/metrics';
import { pendingForSchool } from './students';

export const NEAR_FULL_AT = 27;
export const QUIET_DAYS = 14;
const DAY = 24 * 60 * 60 * 1000;

export interface AttentionClass {
  id: string;
  class_name: string;
  archived?: boolean;
  section?: string;
  class_teacher_id?: string | null;
  teacher_ids?: string[];
  student_ids?: string[];
}

export interface AttentionPerson {
  id: string;
  approvalStatus: string | null;
  classIds: string[];
  /** Students: the class asked for at signup, until approved. */
  requestedClassId?: string | null;
}

export type AttentionKind =
  | 'pending_teachers'
  | 'students_awaiting_school'
  | 'no_class_teacher'
  | 'near_full'
  | 'unplaced_students'
  | 'teachers_without_classes'
  | 'quiet_teachers'
  | 'quiet_classes';

export interface AttentionItem {
  kind: AttentionKind;
  /** Ids of the teachers, classes or students concerned. */
  ids: string[];
  /** 'act' needs a decision; 'watch' is worth a look. */
  severity: 'act' | 'watch';
}

/** Everything that needs a decision or a look, most pressing first. Empty kinds are left out. */
export function needsAttention(
  input: {
    classes: AttentionClass[];
    teachers: AttentionPerson[];
    students: AttentionPerson[];
    results: Pick<Result, 'classId' | 'teacherId' | 'completedAt'>[];
  },
  now = Date.now()
): AttentionItem[] {
  const { classes, teachers, students, results } = input;
  const liveClassIds = new Set(classes.map((c) => c.id));
  const approved = teachers.filter((t) => t.approvalStatus === 'approved');
  const approvedIds = new Set(approved.map((t) => t.id));
  const since = now - QUIET_DAYS * DAY;
  const recent = results.filter((r) => r.completedAt !== null && r.completedAt >= since);
  const activeClasses = new Set(recent.map((r) => r.classId).filter(Boolean));
  const activeTeachers = new Set(recent.map((r) => r.teacherId).filter(Boolean));

  const teaching = new Map<string, number>();
  classes.forEach((c) => (c.teacher_ids ?? []).forEach((t) => teaching.set(t, (teaching.get(t) ?? 0) + 1)));

  const items: AttentionItem[] = [
    {
      kind: 'pending_teachers',
      severity: 'act',
      ids: teachers.filter((t) => (t.approvalStatus ?? 'pending') === 'pending').map((t) => t.id),
    },
    {
      // Pending students no class teacher can approve: the school must decide.
      kind: 'students_awaiting_school',
      severity: 'act',
      ids: pendingForSchool(
        students.map((s) => ({ ...s, requestedClassId: s.requestedClassId ?? null })),
        classes
      ).map((s) => s.id),
    },
    {
      kind: 'no_class_teacher',
      severity: 'act',
      // A class teacher who is no longer approved cannot approve students either.
      ids: classes.filter((c) => !c.class_teacher_id || !approvedIds.has(c.class_teacher_id)).map((c) => c.id),
    },
    {
      kind: 'unplaced_students',
      severity: 'act',
      // Approved but in no class. A pending student is placed by approving them.
      ids: students
        .filter((s) => s.approvalStatus === 'approved' && !s.classIds.some((id) => liveClassIds.has(id)))
        .map((s) => s.id),
    },
    {
      kind: 'near_full',
      severity: 'watch',
      ids: classes.filter((c) => (c.student_ids ?? []).length >= NEAR_FULL_AT).map((c) => c.id),
    },
    {
      kind: 'teachers_without_classes',
      severity: 'watch',
      ids: approved.filter((t) => !teaching.has(t.id)).map((t) => t.id),
    },
    {
      kind: 'quiet_teachers',
      severity: 'watch',
      ids: approved.filter((t) => teaching.has(t.id) && !activeTeachers.has(t.id)).map((t) => t.id),
    },
    {
      kind: 'quiet_classes',
      severity: 'watch',
      ids: classes.filter((c) => (c.student_ids ?? []).length > 0 && !activeClasses.has(c.id)).map((c) => c.id),
    },
  ];
  return items.filter((i) => i.ids.length > 0);
}

export interface ClassActivity {
  classId: string;
  /** Distinct live-class sessions that produced results in the period. */
  lessons: number;
  /** Distinct students with a result in the period. */
  activeStudents: number;
  average: number | null;
  lastActivity: number | null;
}

/** Per-class activity in a period; every class gets a row, active or not. */
export function classActivity(
  classIds: string[],
  results: Result[],
  range: DateRange | null,
  passThreshold?: number
): Map<string, ClassActivity> {
  const byClass = new Map<string, Result[]>();
  for (const r of results) {
    if (!r.classId || !withinRange(r.completedAt, range)) continue;
    if (!byClass.has(r.classId)) byClass.set(r.classId, []);
    byClass.get(r.classId)!.push(r);
  }
  // Last activity is all-time: "last lesson 3 months ago" is the point of the column.
  const last = new Map<string, number>();
  for (const r of results) {
    if (!r.classId || r.completedAt === null) continue;
    last.set(r.classId, Math.max(last.get(r.classId) ?? 0, r.completedAt));
  }
  return new Map(
    classIds.map((id) => {
      const list = byClass.get(id) ?? [];
      return [
        id,
        {
          classId: id,
          lessons: new Set(list.map((r) => r.sessionId).filter(Boolean)).size,
          activeStudents: new Set(list.map((r) => r.studentId)).size,
          average: list.length ? summarise(list, passThreshold).average : null,
          lastActivity: last.get(id) ?? null,
        },
      ];
    })
  );
}
